/**
 * @zendir/ui - Select Component
 *
 * Dropdown select following Astro UX Design System.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Spacecraft"
 *   options={[
 *     { value: 'sat-001', label: 'Explorer-1' },
 *     { value: 'sat-002', label: 'Voyager-2' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 * ```
 */

import React, {
  memo,
  forwardRef,
  useState,
  useId,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";
import type { LabelPlacement } from "./Input";
import { useBreakpoint } from "../layout/useBreakpoint";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  /** Label text or ReactNode (supports rich content like tooltip icons) */
  label?: React.ReactNode;
  /** Options array */
  options: SelectOption[];
  /** Selected value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Size variant */
  size?: "small" | "medium" | "large";
  /** Custom className */
  className?: string;
  /**
   * Label placement style.
   * - `'outlined'` — label sits on the input border (notched/Material-style). **Default.**
   * - `'above'`    — label sits above the input with a gap (classic stack).
   */
  labelPlacement?: LabelPlacement;
  /** Required indicator */
  required?: boolean;
}

export const Select = memo(
  forwardRef<HTMLDivElement, SelectProps>(function Select(
    {
      label,
      options,
      value,
      onChange,
      placeholder = "Select...",
      helperText,
      error,
      disabled = false,
      fullWidth = false,
      size = "medium",
      className = "",
      labelPlacement = "outlined",
      required,
    },
    ref,
  ): React.ReactElement {
    const { tokens, theme } = useTheme();
    const { isMobile } = useBreakpoint();
    const [isOpen, setIsOpen] = useState(false);
    const [focused, setFocused] = useState(false);
    const [dropdownRect, setDropdownRect] = useState<{
      top: number;
      left: number;
      width: number;
      openUp?: boolean;
    } | null>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const generatedId = useId();

    // Transparent theme detection (purple-hue transparent bg)
    const isTransparentTheme =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const _transparentDefault = tokens.colors.interactive.transparentDefault;
    const _transparentInputBg = tokens.colors.interactive.transparentInputBg;

    const hasError = !!error;
    const selectedOption = options.find((o) => o.value === value);
    const isOutlined = labelPlacement === "outlined";

    const sizeConfig = {
      small: {
        height: tokens.elementSize.sm,
        fontSize: tokens.typography.fontSize.xs,
        padding: tokens.spacing.sm,
        inputPaddingLeft: 8,
      },
      medium: {
        height: tokens.elementSize.md,
        fontSize: tokens.typography.fontSize.sm,
        padding: tokens.spacing.md,
        inputPaddingLeft: 16,
      },
      large: {
        height: tokens.elementSize.lg,
        fontSize: tokens.typography.fontSize.base,
        padding: tokens.spacing.lg,
        inputPaddingLeft: 24,
      },
    };
    const config = sizeConfig[size];
    const controlHeight = isMobile ? "44px" : config.height;
    const optionRowHeight = isMobile
      ? 40
      : size === "small"
        ? 30
        : size === "medium"
          ? 34
          : 38;

    // Position dropdown when open (for portal) - keep in viewport
    const DROPDOWN_MAX_HEIGHT = 200;
    const GAP = 4;
    useLayoutEffect(() => {
      if (!isOpen || !triggerRef.current) {
        setDropdownRect(null);
        return;
      }
      const update = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom - GAP;
          const spaceAbove = rect.top - GAP;
          const openUp =
            spaceBelow <
              Math.min(DROPDOWN_MAX_HEIGHT, options.length * optionRowHeight) &&
            spaceAbove > spaceBelow;
          setDropdownRect({
            top: openUp ? rect.top - GAP : rect.bottom + GAP,
            left: rect.left,
            width: rect.width,
            openUp,
          });
        }
      };
      update();
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [isOpen, options.length, optionRowHeight]);

    // Close dropdown on outside click (include portaled dropdown)
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        // Check if click is inside the trigger or the portaled dropdown
        if (
          selectRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setIsOpen(false);
      };
      // Use click instead of mousedown to avoid race condition with option onClick
      document.addEventListener("click", handleClickOutside, true);
      return () =>
        document.removeEventListener("click", handleClickOutside, true);
    }, [isOpen]);

    const computedBorder = hasError
      ? tokens.borders.input.error
      : focused || isOpen
        ? tokens.borders.input.focus
        : tokens.borders.input.default;

    // Background for the outlined label "notch"
    const labelBg = isTransparentTheme
      ? tokens.colors.background.base
      : tokens.colors.background.surface;

    // Outlined label font sizes
    const outlinedLabelSizeMap = {
      small: "0.625rem",
      medium: "0.6875rem",
      large: "0.75rem",
    };
    const outlinedLabelLeftMap = {
      small: "2px",
      medium: "10px",
      large: "18px",
    };

    // Shared label content
    const labelContent = (
      <>
        {label}
        {required && (
          <span
            style={{ color: tokens.colors.status.critical, marginLeft: "2px" }}
          >
            *
          </span>
        )}
      </>
    );

    return (
      <div
        ref={ref}
        className={classNames("zendir-select-wrapper", className)}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.xs,
          width: fullWidth ? "100%" : undefined,
        }}
      >
        {/* Label: 'above' variant */}
        {label && !isOutlined && (
          <span
            id={`${generatedId}-label`}
            style={{
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.text.secondary,
            }}
          >
            {labelContent}
          </span>
        )}

        <div ref={selectRef} style={{ position: "relative" }}>
          {/* Label: 'outlined' variant — positioned on the trigger border */}
          {label && isOutlined && (
            <span
              id={`${generatedId}-label`}
              style={{
                position: "absolute",
                top: 0,
                left: outlinedLabelLeftMap[size],
                transform: "translateY(-50%)",
                backgroundColor: labelBg,
                padding: `0 ${tokens.spacing.xs}`,
                fontSize: outlinedLabelSizeMap[size],
                fontWeight: tokens.typography.fontWeight.medium,
                color:
                  focused || isOpen
                    ? hasError
                      ? tokens.colors.status.critical
                      : tokens.colors.accent.primary
                    : hasError
                      ? tokens.colors.status.critical
                      : tokens.colors.text.secondary,
                zIndex: 1,
                pointerEvents: "none",
                transition: `color ${tokens.animation.fast}`,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                letterSpacing: "0.01em",
              }}
            >
              {labelContent}
            </span>
          )}

          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby={label ? `${generatedId}-label` : undefined}
            aria-describedby={
              helperText || error ? `${generatedId}-hint` : undefined
            }
            style={{
              width: "100%",
              height: controlHeight,
              padding: `0 ${config.padding}`,
              fontSize: config.fontSize,
              fontFamily: tokens.typography.fontFamily.primary,
              color: selectedOption
                ? tokens.colors.text.primary
                : tokens.colors.text.muted,
              backgroundColor: isTransparentTheme
                ? "rgba(10, 15, 25, 0.4)"
                : tokens.colors.background.surface,
              border: computedBorder,
              borderRadius: tokens.borderRadius.md,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              textAlign: "left",
              outline: "none",
              transition: `border-color ${tokens.animation.fast}`,
              boxShadow:
                focused || isOpen ? tokens.borders.focusRing.default : "none",
            }}
          >
            <span>{selectedOption?.label ?? placeholder}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={tokens.colors.text.muted}
              style={{
                transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                transition: `transform ${tokens.animation.fast}`,
              }}
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>

          {isOpen &&
            dropdownRect &&
            createPortal(
              <ul
                ref={dropdownRef}
                role="listbox"
                style={{
                  position: "fixed",
                  top: dropdownRect.openUp ? undefined : dropdownRect.top,
                  bottom: dropdownRect.openUp
                    ? window.innerHeight - dropdownRect.top
                    : undefined,
                  left: dropdownRect.left,
                  width: dropdownRect.width,
                  padding: tokens.spacing.xs,
                  backgroundColor: isTransparentTheme
                    ? `${tokens.colors.accent.primary}1f`
                    : tokens.colors.background.elevated,
                  backdropFilter: isTransparentTheme ? "blur(12px)" : undefined,
                  WebkitBackdropFilter: isTransparentTheme
                    ? "blur(12px)"
                    : undefined,
                  border: tokens.borders.dropdown,
                  borderRadius: tokens.borderRadius.md,
                  boxShadow: `${tokens.shadows.lg}, 0 0 30px ${tokens.colors.accent.primary}10`,
                  zIndex: 10000,
                  maxHeight: "200px",
                  overflowY: "auto",
                  listStyle: "none",
                  margin: 0,
                  animation: `zendir-dropdown ${tokens.animation.duration.normal}ms ${tokens.animation.easing.default} both`,
                }}
              >
                <style>{`@keyframes zendir-dropdown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                {options.map((option) => (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    tabIndex={option.disabled ? -1 : 0}
                    onMouseDown={(e) => {
                      // Prevent the document click handler from firing before we process this
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!option.disabled) {
                        onChange?.(option.value);
                        setIsOpen(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (option.disabled) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onChange?.(option.value);
                        setIsOpen(false);
                      }
                    }}
                    style={{
                      padding: `0 ${tokens.spacing.md}`,
                      height: optionRowHeight,
                      minHeight: optionRowHeight,
                      display: "flex",
                      alignItems: "center",
                      fontSize: config.fontSize,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: option.disabled
                        ? tokens.colors.text.muted
                        : tokens.colors.text.primary,
                      backgroundColor:
                        option.value === value
                          ? `${tokens.colors.accent.primary}15`
                          : "transparent",
                      borderRadius: tokens.borderRadius.sm,
                      cursor: option.disabled ? "not-allowed" : "pointer",
                      opacity: option.disabled ? 0.5 : 1,
                      transition: `background-color ${tokens.animation.fast}`,
                    }}
                    onMouseEnter={(e) => {
                      if (!option.disabled) {
                        e.currentTarget.style.backgroundColor = `${tokens.colors.accent.primary}10`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        option.value === value
                          ? `${tokens.colors.accent.primary}15`
                          : "transparent";
                    }}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>,
              document.body,
            )}
        </div>

        {(helperText || error) && (
          <span
            id={`${generatedId}-hint`}
            role={hasError ? "alert" : undefined}
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: hasError
                ? tokens.colors.status.critical
                : tokens.colors.text.muted,
            }}
          >
            {error || helperText}
          </span>
        )}
      </div>
    );
  }),
);

export default Select;
